import qs from 'qs';

import { AbstractService } from 'src/services/AbstractService';
import { config } from 'src/lib/config';
import { ServiceError } from 'src/lib/errors';
import { DataSource } from 'src/constants';

class ContentStoreTagsApi extends AbstractService implements Services.ContentStoreApi.Tags {
    public async createTag(
        bearerToken: string | undefined | false,
        body: Models.ContentStoreApi.V3.CreateTag,
        signal?: AbortSignal,
    ): Promise<Models.ContentStoreApi.V3.Tag> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request createTag. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/entities/tags`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            const json = await this.api
                .post(url, {
                    json: body,
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.ContentStoreApi.V3.Tag>();

            return json;
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to create tag',
                info: (e as Error).message,
                url,
                searchParams,
            });
        }
    }

    public async deleteTag(
        bearerToken: string | undefined | false,
        id: string,
        signal?: AbortSignal,
    ): Promise<void> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request deleteTag. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/entities/tags/${id}`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            await this.api
                .delete(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                });
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to delete tag',
                info: (e as Error).message,
                url,
                id,
                searchParams,
            });
        }
    }

    public async getTag(
        bearerToken: string | undefined | false,
        id: string,
        signal?: AbortSignal,
    ): Promise<Models.ContentStoreApi.V3.SourcedTag> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request getTag. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/entities/tags/${id}`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            const json = await this.api
                .get(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.ContentStoreApi.V3.Tag>();

            return {
                ...json,
                source: DataSource.Saved,
            };
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to fetch tag',
                info: (e as Error).message,
                url,
                id,
                searchParams,
            });
        }
    }

    public async getTags(
        bearerToken: string | undefined | false,
        ids?: string[],
        label?: string,
        offset?: number,
        limit?: number,
        signal?: AbortSignal,
    ): Promise<Models.V3.PageResult<Models.ContentStoreApi.V3.SourcedTag>> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request getTags. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/entities/tags`;
        const searchParams = qs.stringify({
            label,
            offset,
            limit,
            entityTagIds: ids,
            requestor: config.appName,
        }, { arrayFormat: 'repeat' });

        try {
            const json = await this.api
                .get(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.V3.PageResult<Models.ContentStoreApi.V3.Tag>>();

            return {
                ...json,
                results: json.results.map((item) => ({
                    ...item,
                    source: DataSource.Saved,
                })),
            };
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to get tags',
                info: (e as Error).message,
                url,
                searchParams,
            });
        }
    }

    public async updateTag(
        bearerToken: string | undefined | false,
        id: string,
        body: Models.ContentStoreApi.V3.UpdateTag,
        signal?: AbortSignal,
    ): Promise<Models.ContentStoreApi.V3.Tag> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request updateTag. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/entities/tags/${id}`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            const json = await this.api
                .put(url, {
                    signal,
                    json: body,
                    searchParams,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.ContentStoreApi.V3.Tag>();

            return json;
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to update tag',
                info: (e as Error).message,
                url,
                searchParams,
            });
        }
    }
}

export const ContentStoreTagsService = new ContentStoreTagsApi(config.services.contentStoreApi);
